/**
 * treatmentFieldValue — pure helpers shared by the export preview so the
 * on-screen document and the server PDF (functions/treatmentPdfHtmlTemplate.js)
 * apply the same rules to `treatment.fields[]`:
 *
 *   section_header → heading only (never has a value)
 *   checkbox       → "כן" / "לא"
 *   multi_select   → "a, b"
 *   date           → dd/mm/yyyy (value is already a yyyy-mm-dd string)
 *   long_text      → sanitized HTML from the execCommand RichTextEditor
 *   everything else→ plain text
 *
 * Keep the sanitizer allow-list identical to `sanitizeFieldHtml` on the server.
 */

import { formatCalendarIsoToDdMmYyyy } from "../../../utils/dateUtils";
import { decodeHtmlEntities } from "../../../utils/htmlEntities";

const ALLOWED_TAGS = new Set([
  "p", "div", "span", "font", "br", "b", "strong", "i", "em", "u", "s",
  "ul", "ol", "li", "h1", "h2", "h3", "blockquote", "hr",
]);
const TEXT_SIZE_CLASS = /^text-size-(normal|subheading|heading)$/;
const COLOR_VALUE = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\))$/i;
const MAX_FIELD_HTML = 200 * 1024;

const sanitizeStyle = (style) => {
  const out = [];
  String(style || "")
    .split(";")
    .forEach((decl) => {
      const [prop, ...rest] = decl.split(":");
      const name = (prop || "").trim().toLowerCase();
      const value = rest.join(":").trim();
      if (!name || !value) return;
      if (name === "color" && COLOR_VALUE.test(value)) out.push(`color: ${value}`);
      else if (name === "text-align" && /^(left|right|center|justify|start|end)$/i.test(value)) {
        out.push(`text-align: ${value.toLowerCase()}`);
      } else if (name === "font-weight" && /^(bold|normal|[1-9]00)$/i.test(value)) {
        out.push(`font-weight: ${value.toLowerCase()}`);
      }
    });
  return out.join("; ");
};

const cleanNode = (node, doc) => {
  Array.from(node.childNodes).forEach((child) => {
    if (child.nodeType === 3) return; // text
    if (child.nodeType !== 1) {
      child.remove();
      return;
    }
    const tag = child.tagName.toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep text content of unknown/unsafe elements (never their markup)
      const text = doc.createTextNode(child.textContent || "");
      child.replaceWith(text);
      return;
    }
    const style = sanitizeStyle(child.getAttribute("style"));
    const cls = child.getAttribute("class");
    const fontColor = tag === "font" ? child.getAttribute("color") : null;
    Array.from(child.attributes).forEach((a) => child.removeAttribute(a.name));
    if (tag === "font") {
      // <font color> → <span style="color">
      const span = doc.createElement("span");
      if (fontColor && COLOR_VALUE.test(fontColor)) span.setAttribute("style", `color: ${fontColor}`);
      while (child.firstChild) span.appendChild(child.firstChild);
      child.replaceWith(span);
      cleanNode(span, doc);
      return;
    }
    if (style) child.setAttribute("style", style);
    if (cls && TEXT_SIZE_CLASS.test(cls.trim())) child.setAttribute("class", cls.trim());
    cleanNode(child, doc);
  });
};

/** Browser-side sanitizer (DOMParser). Same allow-list as the server. */
export const sanitizeFieldHtml = (raw) => {
  let html = String(raw || "");
  if (!html) return "";
  if (html.length > MAX_FIELD_HTML) html = html.slice(0, MAX_FIELD_HTML);
  if (typeof DOMParser === "undefined") return "";
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  doc.querySelectorAll("script,style,iframe,object,embed,img,svg,link,meta").forEach((n) => n.remove());
  cleanNode(doc.body, doc);
  return doc.body.innerHTML.trim();
};

const htmlToText = (html) => {
  if (typeof DOMParser === "undefined") return String(html || "");
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  return (doc.body.textContent || "").trim();
};

export const isFieldEmpty = (field) => {
  if (!field || field.type === "section_header") return false;
  const v = field.value;
  if (v === undefined || v === null || v === "") return true;
  if (field.type === "checkbox") return false; // false is a real answer ("לא")
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "string") {
    if (v.includes("<")) return htmlToText(v) === "";
    return v.trim() === "";
  }
  return false;
};

/**
 * @returns {{ kind: "html", html: string } | { kind: "text", text: string }}
 */
export const fieldDisplayValue = (field) => {
  const v = field?.value;
  if (field?.type === "checkbox") {
    return { kind: "text", text: v === true || v === "true" ? "כן" : "לא" };
  }
  if (Array.isArray(v)) return { kind: "text", text: decodeHtmlEntities(v.join(", ")) };
  if (field?.type === "date") return { kind: "text", text: formatCalendarIsoToDdMmYyyy(v) };
  if (typeof v === "string" && v.includes("<")) {
    return { kind: "html", html: sanitizeFieldHtml(v) };
  }
  // Tag-less rich-text may still carry `&nbsp;` etc. — decode so the preview
  // matches the server PDF (which decodes before escaping).
  return { kind: "text", text: v == null ? "" : decodeHtmlEntities(String(v)) };
};
